import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import { animeList, articles } from '@/mocks/anime';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const anime = animeList.find((a) => a.id === id);
  const article = articles.find((a) => a.id === id);
  const content = anime || article;

  if (!content) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-4xl mx-auto px-4 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-950">未找到内容</h2>
            <p className="text-foreground-600 mt-2">该内容不存在或已被删除</p>
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="inline-block mt-6 px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              返回首页
            </a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const isAnime = 'episodes' in content;
  const isArticleContent = 'excerpt' in content && !('episodes' in content);
  const contentTitle = content.title;

  return (
    <>
      <SEO
        title={contentTitle}
        description={isAnime ? (content as typeof animeList[0]).description : (content as typeof articles[0]).excerpt}
        keywords={`${contentTitle},天天动漫,在线观看,番剧详情,动漫推荐`}
        canonicalPath={`/article/${id}`}
        ogImage={content.cover}
        ogType="article"
        articlePublishedTime={isAnime ? `${(content as typeof animeList[0]).year}-01-01` : (content as typeof articles[0]).date}
        articleModifiedTime={isAnime ? `${(content as typeof animeList[0]).year}-01-01` : (content as typeof articles[0]).date}
      />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      {isArticleContent && (
        <SchemaOrg
          type="Article"
          data={{
            headline: contentTitle,
            description: (content as typeof articles[0]).excerpt,
            image: content.cover,
            datePublished: (content as typeof articles[0]).date,
            dateModified: (content as typeof articles[0]).date,
            author: { '@type': 'Person', name: (content as typeof articles[0]).author },
          }}
        />
      )}

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb items={[
              { name: '首页', href: '/' },
              { name: isAnime ? '番剧详情' : '动漫资讯', href: isAnime ? `/category/${(content as typeof animeList[0]).category}` : '/' },
              { name: contentTitle },
            ]} />

            <header className="mt-6">
              {isAnime && (
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {(content as typeof animeList[0]).tags.map((tag: string) => (
                    <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              {isArticleContent && (
                <div className="mb-4">
                  <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full whitespace-nowrap">
                    {(content as typeof articles[0]).category}
                  </span>
                </div>
              )}
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight">
                {contentTitle}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-600">
                {isAnime && (
                  <>
                    <span className="flex items-center gap-1">
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill text-accent-500"></i></span>
                      {(content as typeof animeList[0]).rating} 分
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-eye-line"></i></span>
                      {((content as typeof animeList[0]).views / 10000).toFixed(1)} 万次观看
                    </span>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded whitespace-nowrap ${
                      (content as typeof animeList[0]).status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
                    }`}>
                      {(content as typeof animeList[0]).status}
                    </span>
                  </>
                )}
                {isArticleContent && (
                  <>
                    <span className="flex items-center gap-1">
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-line"></i></span>
                      {(content as typeof articles[0]).author}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line"></i></span>
                      {(content as typeof articles[0]).date}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-eye-line"></i></span>
                      {((content as typeof articles[0]).views / 10000).toFixed(1)} 万阅读
                    </span>
                  </>
                )}
              </div>
            </header>

            {isAnime && (
              <div className="mt-8 flex flex-col md:flex-row gap-8">
                <div className="flex-shrink-0 w-full sm:w-64">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-100">
                    <img
                      src={content.cover}
                      alt={contentTitle}
                      title={contentTitle}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="bg-background-100 rounded-lg p-4">
                      <p className="text-xs text-foreground-500 mb-1">更新进度</p>
                      <p className="text-sm font-medium text-foreground-950">{(content as typeof animeList[0]).episodes}</p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-4">
                      <p className="text-xs text-foreground-500 mb-1">更新时间</p>
                      <p className="text-sm font-medium text-foreground-950">{(content as typeof animeList[0]).updateInfo}</p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-4">
                      <p className="text-xs text-foreground-500 mb-1">播出年份</p>
                      <p className="text-sm font-medium text-foreground-950">{(content as typeof animeList[0]).year}</p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-4">
                      <p className="text-xs text-foreground-500 mb-1">状态</p>
                      <p className="text-sm font-medium text-foreground-950">{(content as typeof animeList[0]).status}</p>
                    </div>
                  </div>
                  <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed">
                    <p>{(content as typeof animeList[0]).description}</p>
                  </div>
                  <div className="mt-6">
                    <button
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                    >
                      <span className="w-4 h-4 inline-flex items-center justify-center mr-2"><i className="ri-play-circle-line"></i></span>
                      开始观看
                    </button>
                  </div>
                </div>
              </div>
            )}

            {isArticleContent && (
              <>
                <div className="mt-8 aspect-video rounded-xl overflow-hidden bg-background-100">
                  <img
                    src={content.cover}
                    alt={contentTitle}
                    title={contentTitle}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div
                  className="mt-8 prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed [&_h2]:text-xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-10 [&_h2]:mb-4 [&_h3]:text-lg [&_h3]:font-heading [&_h3]:font-bold [&_h3]:text-foreground-950 [&_h3]:mt-8 [&_h3]:mb-3 [&_p]:mb-4 [&_p]:leading-8 [&_strong]:text-foreground-950 [&_ul]:mb-4 [&_li]:mb-2"
                  dangerouslySetInnerHTML={{ __html: (content as typeof articles[0]).content }}
                />
              </>
            )}

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500 whitespace-nowrap">标签：</span>
                {isAnime && (content as typeof animeList[0]).tags.map((tag: string) => (
                  <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
                {isArticleContent && (content as typeof articles[0]).tags.map((tag: string) => (
                  <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          <div className="max-w-4xl mx-auto px-4 md:px-6 pb-8">
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
              返回首页
            </a>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}